// src/components/PortalVerificacao/PainelVerificacao.tsx
// UPDATED: 2026-06-09 — v2.1
// ✅ Barra de progresso sem emojis — ícones Lucide
// ✅ Conector preenchido corretamente (etapas anteriores à ativa)
// ✅ payment_pending vai direto para etapa 3
// ✅ pending começa na etapa 1 (sem forçar step 2)

import React, { useState } from 'react';
import {
  CheckCircle, Clock, XCircle, CreditCard, Shield,
  RefreshCw, Copy, Check, Key, Eye, EyeOff, Lock,
  LogIn, ExternalLink, MessageCircle,
  Inbox, Search, Wallet, BadgeCheck,
} from 'lucide-react';
import { VerificacaoData, STATUS_CONFIG } from '../../services/verificacaoService';
import SecaoPagamento     from './SecaoPagamento';
import SecaoVideos        from './SecaoVideos';
import SecaoDocumentos    from './SecaoDocumentos';
import SecaoDadosCadastro from './SecaoDadosCadastro';
import ExportarPDF        from './ExportarPDF';

const BRAND = { gold: '#D4AF37', goldDeep: '#B88917', charcoal: '#1F2328' };

const PORTAL_FILIADO_URL = import.meta.env.VITE_PORTAL_FILIADO_URL || '/portal/filiado';
const PORTAL_PRF_URL     = import.meta.env.VITE_PORTAL_PRF_URL     || '/login/filiado';
const WHATSAPP_URL       = import.meta.env.VITE_WHATSAPP_URL       || 'https://wa.me/5511999999999';

interface Props {
  data:          VerificacaoData;
  protocolo:     string;
  onAtualizar:   () => void;
  onExportarPDF?: () => void | Promise<void>;
  exportando?:   boolean;
}

// ── Barra de progresso ────────────────────────────────────────
// step: 1=Recebido 2=Em análise 3=Pagamento 4=Ativo
// Regra: conector entre A→B fica preenchido se B já foi alcançado (displayStep > A)

const ETAPAS = [
  { label: 'Recebido',   Icon: Inbox       },
  { label: 'Em análise', Icon: Search      },
  { label: 'Pagamento',  Icon: Wallet      },
  { label: 'Ativo',      Icon: BadgeCheck  },
];

const BarraProgresso: React.FC<{ step: number; hasPaymentLink?: boolean }> = ({ step, hasPaymentLink }) => {
  const displayStep = hasPaymentLink && step < 3 ? 3 : step === 1 ? 2 : step;


  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {ETAPAS.map((etapa, i) => {
          const etapaNum = i + 1;
          const completo = etapaNum < displayStep;
          const ativo    = etapaNum === displayStep;

          // Conector após este item: preenchido se a próxima etapa já foi alcançada
          const proximaAlcancada = displayStep > etapaNum;

          return (
            <React.Fragment key={etapa.label}>
              {/* Bolinha */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: completo || ativo ? 'white' : '#9ca3af',
                    background: completo
                      ? BRAND.charcoal
                      : ativo
                        ? `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDeep})`
                        : '#f3f4f6',
                    border: ativo ? `2.5px solid ${BRAND.gold}` : '2px solid transparent',
                    boxShadow: ativo ? `0 0 0 4px ${BRAND.gold}25` : 'none',
                    animation: ativo ? 'pulse-ring 2s cubic-bezier(0.4,0,0.6,1) infinite' : 'none',
                    transition: 'all 0.3s',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {completo
                    ? <Check style={{ width: 18, height: 18, strokeWidth: 3 }} />
                    : <etapa.Icon style={{ width: 17, height: 17 }} />}
                </div>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: ativo ? 700 : 500,
                    color: ativo ? BRAND.goldDeep : completo ? BRAND.charcoal : '#9ca3af',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.01em',
                  }}
                >
                  {etapa.label}
                </span>
              </div>

              {/* Conector */}
              {i < ETAPAS.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 3,
                    borderRadius: 99,
                    background: '#f3f4f6',
                    position: 'relative',
                    marginBottom: 22,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: proximaAlcancada ? '100%' : '0%',
                      background: BRAND.charcoal,
                      transition: 'width 0.6s ease',
                      borderRadius: 99,
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <style>{`
        @keyframes pulse-ring {
          0%, 100% { box-shadow: 0 0 0 4px ${BRAND.gold}25; }
          50%       { box-shadow: 0 0 0 8px ${BRAND.gold}10; }
        }
      `}</style>
    </div>
  );
};

// ── Card de dados de acesso ───────────────────────────────────
const CardAcesso: React.FC<{ login: string | null; password: string | null }> = ({ login, password }) => {
  const [senhaMostrada, setSenhaMostrada] = useState(false);
  const [copiado,       setCopiado]       = useState<string | null>(null);

  const copiar = async (texto: string, key: string) => {
    try { await navigator.clipboard.writeText(texto); } catch {}
    setCopiado(key);
    setTimeout(() => setCopiado(null), 1800);
  };

  if (!login) return null;

  return (
    <div
      style={{
        borderRadius: 20,
        border: '1.5px solid #bbf7d0',
        background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
        padding: '1.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div
          style={{
            width: 42, height: 42, borderRadius: 12,
            background: '#dcfce7', border: '1.5px solid #bbf7d0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Key style={{ width: 20, height: 20, color: '#065f46' }} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: BRAND.charcoal }}>
            Seus Dados de Acesso
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#059669' }}>
            Use as credenciais abaixo para entrar no Portal do Filiado
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {/* Login */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'white', borderRadius: 12, border: '1px solid #d1fae5' }}>
          <LogIn style={{ width: 18, height: 18, color: '#6b7280', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              ID Oficial / Protocolo
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 700, color: BRAND.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {login}
            </p>
          </div>
          <button
            onClick={() => copiar(login, 'login')}
            style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #d1fae5', background: '#f0fdf4', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            {copiado === 'login'
              ? <Check style={{ width: 14, height: 14, color: '#059669' }} />
              : <Copy  style={{ width: 14, height: 14, color: '#6b7280' }} />}
          </button>
        </div>

        {/* Senha */}
        {password && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'white', borderRadius: 12, border: '1px solid #d1fae5' }}>
            <Lock style={{ width: 18, height: 18, color: '#6b7280', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Senha
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: BRAND.charcoal }}>
                {senhaMostrada ? password : '••••••••••'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button
                onClick={() => setSenhaMostrada(!senhaMostrada)}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #d1fae5', background: '#f0fdf4', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {senhaMostrada
                  ? <EyeOff style={{ width: 14, height: 14, color: '#6b7280' }} />
                  : <Eye    style={{ width: 14, height: 14, color: '#6b7280' }} />}
              </button>
              <button
                onClick={() => copiar(password, 'senha')}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #d1fae5', background: '#f0fdf4', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {copiado === 'senha'
                  ? <Check style={{ width: 14, height: 14, color: '#059669' }} />
                  : <Copy  style={{ width: 14, height: 14, color: '#6b7280' }} />}
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <a
          href={PORTAL_FILIADO_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', borderRadius: 12, background: BRAND.charcoal, color: 'white', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}
        >
          <LogIn style={{ width: 15, height: 15 }} />
          Portal Filiado
        </a>
        <a
          href={PORTAL_PRF_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', borderRadius: 12, border: `1.5px solid ${BRAND.gold}55`, color: BRAND.charcoal, background: 'white', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}
        >
          <ExternalLink style={{ width: 15, height: 15 }} />
          Portal PRF
        </a>
      </div>
    </div>
  );
};

// ── Badge de status ───────────────────────────────────────────
const StatusBadge: React.FC<{ status: string; cfg: any }> = ({ status, cfg }) => {
  const icone = () => {
    switch (status) {
      case 'active':
      case 'payment_confirmed': return <CheckCircle style={{ width: 20, height: 20 }} />;
      case 'payment_pending':   return <CreditCard  style={{ width: 20, height: 20 }} />;
      case 'inactive':
      case 'suspended':
      case 'rejected':          return <XCircle     style={{ width: 20, height: 20 }} />;
      default:                  return <Clock       style={{ width: 20, height: 20 }} />;
    }
  };
  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', borderRadius: 99,
        background: cfg.bg, color: cfg.color,
        fontSize: 14, fontWeight: 700,
        border: `1.5px solid ${cfg.color}33`,
      }}
    >
      {icone()}
      {cfg.label}
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────
const PainelVerificacao: React.FC<Props> = ({ data, protocolo, onAtualizar }) => {
  const [copiado, setCopiado] = useState(false);
  // Se o admin já enviou o link de pagamento mas o status ainda não foi
  // atualizado para payment_pending (registros antigos), exibe como payment_pending.
  const effectiveStatus =
    data.paymentLink && (data.status === 'pending' || data.status === 'under_review')
      ? 'payment_pending'
      : data.status;

  const cfg  = STATUS_CONFIG[effectiveStatus];
  const step = cfg.step;

  // Barra só faz sentido para fluxo normal (step > 0)
  const mostrarBarra = step > 0;

  const copiarProtocolo = async () => {
    try { await navigator.clipboard.writeText(protocolo); } catch {}
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1800);
  };

  return (
    <div id="portal-verificacao-painel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Card principal de status ───────────────────────── */}
      <div
        style={{
          background: 'rgba(255,255,255,0.98)',
          borderRadius: 24,
          border: `1px solid ${BRAND.gold}28`,
          padding: '1.75rem',
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        }}
      >
        {/* Barra de progresso */}
        {mostrarBarra && <BarraProgresso step={step} hasPaymentLink={!!data.paymentLink} />}


        {/* Status + ações */}
        <div
          style={{
            display: 'flex', flexWrap: 'wrap',
            alignItems: 'flex-start', justifyContent: 'space-between',
            gap: 16, marginBottom: 16,
          }}
        >
          <div>
            <p style={{ margin: '0 0 6px', fontSize: 10.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Status atual
            </p>
            <StatusBadge status={effectiveStatus} cfg={cfg} />
            <p style={{ margin: '10px 0 0', fontSize: 13.5, color: '#6b7280', lineHeight: 1.5, maxWidth: 340 }}>
              {cfg.description}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={onAtualizar}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: 12,
                border: `1.5px solid ${BRAND.gold}44`, background: 'white',
                color: BRAND.charcoal, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <RefreshCw style={{ width: 15, height: 15 }} />
              Atualizar
            </button>
            <ExportarPDF data={data} protocolo={protocolo} variant="compact" />
          </div>
        </div>

        {/* Protocolo */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', borderRadius: 14,
            background: `${BRAND.gold}0D`, border: `1px solid ${BRAND.gold}22`,
          }}
        >
          <Shield style={{ width: 16, height: 16, color: BRAND.goldDeep, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Protocolo
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12.5, fontFamily: 'monospace', fontWeight: 700, color: BRAND.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {protocolo}
            </p>
          </div>
          <button
            onClick={copiarProtocolo}
            style={{ padding: '8px 10px', borderRadius: 10, border: `1px solid ${BRAND.gold}44`, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            {copiado
              ? <Check style={{ width: 15, height: 15, color: '#059669' }} />
              : <Copy  style={{ width: 15, height: 15, color: BRAND.charcoal }} />}
          </button>
        </div>
      </div>

      {/* ── Seção de pagamento (payment_pending) ──────────── */}
      <SecaoPagamento data={data} />

      {/* ── Vídeos AWS (liberados após confirmação) ────────── */}
      <SecaoVideos data={data} />

      {/* ── Dados de acesso (active + released) ───────────── */}
      {data.status === 'active' && (
        <CardAcesso
          login={data.systemId}
          password="fil@feder26"
        />
      )}

      {/* ── Dados cadastrados ──────────────────────────────── */}
      <SecaoDadosCadastro data={data} />

      {/* ── Documentos enviados ────────────────────────────── */}
      <SecaoDocumentos documentos={data.documentos ?? []} />

      {/* ── Exportar PDF completo ──────────────────────────── */}
      <ExportarPDF data={data} protocolo={protocolo} variant="full" />

      {/* ── Rodapé ─────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap',
          alignItems: 'center', justifyContent: 'space-between',
          gap: 12, paddingTop: 8, paddingBottom: 16,
        }}
      >
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="no-print"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '13px 20px', borderRadius: 14,
            background: '#22c55e', color: 'white',
            fontSize: 14, fontWeight: 700, textDecoration: 'none',
            boxShadow: '0 4px 14px rgba(34,197,94,0.3)',
          }}
        >
          <MessageCircle style={{ width: 18, height: 18 }} />
          Falar com o Suporte via WhatsApp
        </a>
        <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
          Cadastrado em {new Date(data.createdAt).toLocaleDateString('pt-BR')}
        </p>
      </div>
    </div>
  );
};

export default PainelVerificacao;